'use client';

/* -------------------------------------------------------------------------- */
/*                                 DEPENDECIES                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import debounce from 'lodash/debounce';
import { useRef, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// UI lib components
import {
  Group,
  Popover,
  Center,
  Button,
  TextInput,
  Text,
  Stack,
  Divider,
  DividerProps,
} from '@mantine/core';

// Local UI dependecies
import SearchSuggestions from '@features/Search/SearchBar/SearchSuggestions';
import ContentLoader from '@shared/ui-kit/molecules/ContentLoader';
import SearchFilters from './SearchFilters';

// Store
import useWorkspace from '@features/Workspace/store/hooks/useWorkspace';
import useViewAs from '@shared/providers/ViewAsProvider/useViewAs';
import useAccount from '@features/Account/store/useAccount';
import { isSpecialUserEmail } from '@shared/constants/users';

// API & Types
import {
  SearchEntityType,
  ResourceFileType,
  SearchResultFragment,
  useSearchLazyQuery,
  useTrackSearchMutation,
} from '@app-types/blackroom-api';

// Assets
import { IconSearch } from '@tabler/icons-react';

// Styles
import * as classes from './styles/index.css';
import * as textInputStylesAPI from './styles/textInputStylesAPI.css';
import * as popoverStylesAPI from './styles/popoverStylesAPI.css';
import { palette } from '@app/shared/ui-kit/colors';

// Constants
const DIVIDER_PROPS: DividerProps = { size: 'xs', color: 'blackroomLightGray' };

/* -------------------------------------------------------------------------- */
/*                                 COMPOENENT                                 */
/* -------------------------------------------------------------------------- */
function SearchBar() {
  /* ********************************** HOOKS ********************************* */

  // State
  const [query, setQuery] = useState<string>('');
  const [instantQuery, setInstantQuery] = useState<string>(''); // To always have the typed value even before the debounce
  const [searchResult, setSearchResult] = useState<SearchResultFragment>();
  const [entityTypeFilters, setEntityTypeFilters] =
    useState<SearchEntityType[]>();
  const [fileTypeFilters, setFileTypeFilters] = useState<ResourceFileType[]>();
  const [isPopoverOpened, setIsPopoverOpened] = useState(false);

  // Store
  const workspace = useWorkspace();
  const { isViewingAs } = useViewAs();
  const { account } = useAccount();

  // Ref
  const inputRef = useRef<HTMLInputElement>(null);
  const ctaClearRef = useRef<any>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const currentSearchIdRef = useRef<number>(0);

  // API
  const [search, { loading: loadingSearch }] = useSearchLazyQuery();
  const [trackSearchMutation] = useTrackSearchMutation();

  // Routing
  const navigate = useNavigate();
  const location = useLocation();

  // Localization
  const { t } = useTranslation('Search');

  /* ***************************** LOCAL VARIABLES **************************** */
  const searchBarBorderColor = 'transparent';

  const performSearch = async () => {
    // Abort previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const searchId = Date.now();
    currentSearchIdRef.current = searchId;

    try {
      const { data: searchData } = await search({
        variables: {
          query,
          filter: {
            workspaceId: workspace.id,
            entityTypes: entityTypeFilters,
            resourceSearchFilter: { fileTypes: fileTypeFilters },
          },
          limit: 5,
        },
        fetchPolicy: 'network-only',
        context: {
          fetchOptions: {
            signal: abortController.signal,
          },
        },
      });

      // Ignore stale responses (safety net if abort didn't work)
      if (currentSearchIdRef.current !== searchId) {
        return;
      }

      setSearchResult(searchData?.search);
    } catch (error) {
      // Silently ignore abort errors
      if (
        error instanceof Error &&
        (error.name === 'AbortError' || error.message?.includes('aborted'))
      ) {
        return;
      }
      console.error('[SearchBar] Search error:', error);
    }
  };

  const clearSearch = () => {
    if (inputRef.current) {
      inputRef.current.blur();
      inputRef.current.value = '';
      setQuery('');
      setEntityTypeFilters(undefined);
      setFileTypeFilters(undefined);
      setIsPopoverOpened(false);
      setSearchResult(undefined);
    }
  };

  const trackSearch = useCallback(() => {
    if (isViewingAs) return;
    if (isSpecialUserEmail(account?.email)) return;
    const searchQuery = instantQuery.trim();
    if (searchQuery) {
      trackSearchMutation({
        variables: { input: { query: searchQuery, workspaceId: workspace.id } },
      });
    }
  }, [
    instantQuery,
    workspace.id,
    trackSearchMutation,
    isViewingAs,
    account?.email,
  ]);

  const setQueryDebouncedInner = useCallback(
    debounce((value: string) => setQuery(value), 500),
    [],
  );

  const handleQueryChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value ?? '';
      setInstantQuery(value);
      setQueryDebouncedInner(value);
    },
    [setQueryDebouncedInner],
  );

  const isValidQuery =
    query && query.length !== 0 && !query.split(' ').every((e) => e === ' ');

  const pageSearchPath = `/workspace/${workspace.id}/search?q=${encodeURIComponent(instantQuery)}${
    entityTypeFilters ? `&entityTypes=${entityTypeFilters.join(',')}` : ''
  }${fileTypeFilters ? `&fileTypes=${fileTypeFilters.join(',')}` : ''}`;

  /* ****************************** SIDE-EFFECTS ****************************** */

  // Perform query search only when query is not empty nor only filled with blanks
  useEffect(() => {
    if (
      query &&
      query.length !== 0 &&
      !query.split(' ').every((e) => e === ' ')
    ) {
      (async function asyncWrapper() {
        await performSearch();
      })();
    } else {
      clearSearch();
    }
  }, [query]);

  // Peform search when filters are updated (even when emptied)
  useEffect(() => {
    (async function ayncWrapper() {
      if ((entityTypeFilters || fileTypeFilters) && isValidQuery) {
        await performSearch();
      }
    })();
  }, [entityTypeFilters, fileTypeFilters]);

  // Reset filters, query and input on workspace switch
  useEffect(() => {
    clearSearch();
  }, [workspace.id]);

  useEffect(() => {
    if (isPopoverOpened) {
      clearSearch();
    }
  }, [location]);

  /* ******************************** RENDERING ******************************* */
  return (
    <Popover
      classNames={{ ...popoverStylesAPI }}
      width="target"
      shadow="lg"
      opened={isPopoverOpened}
      onChange={setIsPopoverOpened}
      keepMounted
    >
      <Popover.Target>
        <TextInput
          style={{
            '--search-bar-border-color': searchBarBorderColor,
          }}
          ref={inputRef}
          classNames={{
            input:
              textInputStylesAPI[isPopoverOpened ? 'inputFocused' : 'input'],
          }}
          w="100%"
          placeholder={t('SearchBar.input.placeholder') as string}
          size="lg"
          leftSection={<IconSearch strokeWidth={1} />}
          color="blackroomGray"
          role="combobox"
          rightSectionWidth={48}
          rightSection={
            <Group gap={0} pr="sm" wrap="nowrap">
              {query ? (
                <Button
                  ref={ctaClearRef}
                  variant="white"
                  bg="transparent"
                  color="blackroomRed"
                  onClick={clearSearch}
                >
                  <Text fw="normal" fz="sm" td="underline">
                    {t('SearchBar.input.ctaClear')}
                  </Text>
                </Button>
              ) : null}
            </Group>
          }
          onChange={handleQueryChange}
          onKeyDown={async (e) => {
            switch (e.key) {
              case 'Enter':
                trackSearch();
                navigate(pageSearchPath);
                clearSearch();
                break;
              default:
                break;
            }
          }}
          onFocus={() => setIsPopoverOpened(true)}
        />
      </Popover.Target>

      <Popover.Dropdown
        p={0}
        style={{
          '--search-bar-border-color': searchBarBorderColor,
        }}
      >
        <Stack gap={0}>
          <Stack className={classes.dropdownMainStack} gap="xs">
            <Text c={palette.blackroomLightGray5}>
              {t('SearchSuggestions.filterTitle')}
            </Text>

            <SearchFilters
              activeFilters={[
                ...(entityTypeFilters ?? []),
                ...(fileTypeFilters ?? []),
              ]}
              onClickFilter={(f) => {
                if (
                  Object.values(SearchEntityType).includes(
                    f as SearchEntityType,
                  )
                ) {
                  if (entityTypeFilters?.includes(f as SearchEntityType)) {
                    setEntityTypeFilters(
                      entityTypeFilters.filter((et) => et !== f),
                    );
                  } else {
                    setEntityTypeFilters([
                      ...(entityTypeFilters ?? []),
                      f as SearchEntityType,
                    ]);
                  }
                } else if (
                  Object.values(ResourceFileType).includes(
                    f as ResourceFileType,
                  )
                ) {
                  if (fileTypeFilters?.includes(f as ResourceFileType)) {
                    setFileTypeFilters(
                      fileTypeFilters.filter((ft) => ft !== f),
                    );
                  } else {
                    setFileTypeFilters([
                      ...(fileTypeFilters ?? []),
                      f as ResourceFileType,
                    ]);
                  }
                }
              }}
            />
          </Stack>

          {loadingSearch || searchResult ? (
            <Divider mb={11} {...DIVIDER_PROPS} />
          ) : null}

          {loadingSearch ? (
            <Center p="md">
              <ContentLoader />
            </Center>
          ) : (
            <SearchSuggestions
              searchResult={searchResult}
              query={query}
              onGoToSearchResults={() => {
                trackSearch();
                navigate(pageSearchPath);
                clearSearch();
              }}
              onClickResult={trackSearch}
            />
          )}
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}

export default SearchBar;
